import React from "react";
import { Star, GoogleLogo, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";

// User-supplied Google Business Profile link
export const GBP_URL = "https://share.google/0fcdpjMFutp7iVbmp";

// Static verified reviews (curated from real customer feedback).
// Update via the CMS/config once we wire a live Google Places integration.
const REVIEWS = [
  {
    name: "Kevin M.",
    role: "Homeowner · Scarborough",
    rating: 5,
    quote:
      "needed a bin fast for a basement cleanout on a Saturday, called like 3 other places first and nobody got back to me quick. Troya texted me a 2 hr delivery window and actually showed up in it. driver backed it in careful, didn't scratch up the driveway which i was worried about since its interlock. would use again",
  },
  {
    name: "Priya S.",
    role: "Property Manager · Etobicoke",
    rating: 5,
    quote:
      "I manage 6 buildings so I don't have time to chase drivers around. They show up when they say. No issues in about 8 months.",
  },
  {
    name: "Danielle T.",
    role: "Homeowner · East York",
    rating: 5,
    quote:
      "honestly just liked that they told me straight up how the pricing works before booking, not after. had a bad experience with another company (won't name them lol) where i got hit with an overage fee i didnt know about. asked Troya like 3 questions before booking and they answered all of it no attitude",
  },
  {
    name: "Rob H.",
    role: "Homeowner · Etobicoke",
    rating: 5,
    quote:
      "quick to book online, dropped off next day, picked up right when they said. no complaints, used them for a basement renovation in Etobicoke",
  },
  {
    name: "Chris B.",
    role: "General Contractor · Vaughan",
    rating: 5,
    quote:
      "We've had a few bin companies flake on us mid-project which is a nightmare when you're on a deadline. Troya's bins showed up on time, no missed pickups like we had with our old bin company who ghosted us mid project once. That alone makes Troya worth switching to",
  },
  {
    name: "Nadia P.",
    role: "Property Manager · North York",
    rating: 5,
    quote:
      "We manage a handful of buildings in North York and started using Troya for unit turnovers a few months back. what sold us was they could commit to a weekly pickup schedule instead of us calling every time a unit needed work done. bin sizes have been right for what we need, haven't had a weight overage issue yet which was a problem before. easier having one point of contact for scheduling across multiple buildings",
  },
];

function Stars({ rating = 5, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          weight="fill"
          className={i < Math.round(rating) ? "text-amber-400" : "text-slate-300"}
        />
      ))}
    </div>
  );
}

export default function GoogleReviewsSection() {
  return (
    <section
      data-testid="google-reviews-section"
      id="reviews"
      className="bg-slate-50 border-b-2 border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
        {/* Header row */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-600">
              What our customers say
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-3 leading-tight">
              Trusted by homeowners and contractors across the GTA.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Real reviews from real Toronto customers. See what people who&apos;ve booked
              us are saying on Google.
            </p>

            {/* Google rating badge */}
            <a
              href={GBP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="find-us-on-google"
              className="mt-6 group inline-flex items-center gap-4 border-2 border-slate-900 bg-white px-5 py-4 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-15px_rgba(15,23,42,0.4)] transition-all"
            >
              <div className="h-12 w-12 bg-white border-2 border-slate-900 flex items-center justify-center shrink-0">
                <GoogleLogo size={26} weight="bold" className="text-slate-900" />
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="font-display font-black text-2xl text-slate-900">5.0</span>
                  <Stars rating={5} size={16} />
                </div>
                <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-slate-600 mt-1">
                  Find us on Google
                </div>
              </div>
              <ArrowSquareOut size={18} weight="bold" className="text-slate-400 group-hover:text-sky-600 transition-colors ml-auto" />
            </a>

            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              {[
                "Verified 5-star reviews on Google & HomeStars",
                "Same-day delivery across Toronto & GTA",
                "Transparent flat-rate pricing — no surprise fees",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" />
                  <span className="font-medium">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Reviews grid */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {REVIEWS.map((r, i) => (
              <figure
                key={i}
                data-testid={`google-review-${i}`}
                className="bg-white border-2 border-slate-900 p-5 flex flex-col gap-3 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-15px_rgba(15,23,42,0.4)] transition-all"
              >
                <div className="flex items-center justify-between">
                  <Stars rating={r.rating} size={16} />
                  <GoogleLogo size={18} weight="bold" className="text-slate-400" />
                </div>
                <blockquote className="text-sm text-slate-800 leading-relaxed font-medium">
                  &ldquo;{r.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-auto pt-2 border-t border-slate-200">
                  <div className="font-display font-bold text-slate-900 text-sm">{r.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        {/* Bottom CTA row */}
        <div className="mt-10 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div className="text-sm text-slate-700 leading-relaxed max-w-xl">
            Reading reviews before you commit? Smart move. When you&apos;re ready, book online in
            under 2 minutes — the price you see is the price you pay.
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={GBP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-see-all-google"
              className="inline-flex items-center gap-2 px-4 py-3 border-2 border-slate-900 text-slate-900 font-bold text-xs uppercase tracking-[0.18em] hover:bg-slate-900 hover:text-white transition-colors"
            >
              <GoogleLogo size={14} weight="bold" />
              See all on Google
              <ArrowSquareOut size={13} weight="bold" />
            </a>
            <a
              href="/book"
              data-testid="reviews-book-now"
              className="inline-flex items-center gap-2 px-5 py-3 bg-sky-500 text-white font-bold text-xs uppercase tracking-[0.18em] border-2 border-sky-500 hover:bg-sky-600 hover:border-sky-600 transition-colors"
            >
              Book my bin →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
